import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import emailjs from '@emailjs/browser';

const ContactFormModal = ({ isOpen, onClose, isPage = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    jobTitle: '',
    phone: '',
    inquiry: '',
    country: '',
    organization: '',
    assistance: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  // Close modal on Escape key
  useEffect(() => {
    if (isPage) return; // Skip modal behavior when used as page

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isPage]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  // Split name into firstName and lastName
  const splitName = (fullName) => {
    const nameParts = fullName.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    return { firstName, lastName };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    const newErrors = {};

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      setSubmitStatus(null);

      // Initialize EmailJS with public key
      emailjs.init('7xFokPIDuwXZrPl34');

      // Split name into firstName and lastName
      const { firstName, lastName } = splitName(formData.name);

      // Prepare template parameters
      const templateParams = {
        first_name: firstName,
        last_name: lastName,
        email: formData.email,
        phone: formData.phone || 'Nicht angegeben',
        company: formData.company,
        position: formData.jobTitle || 'Nicht angegeben',
        inquiry_type: formData.inquiry || 'Nicht angegeben',
        country: formData.country || 'Nicht angegeben',
        org_type: formData.organization || 'Nicht angegeben',
        support_topic: formData.assistance || 'Nicht angegeben',
        message: formData.message || 'Keine Nachricht'
      };

      try {
        // Send email using EmailJS
        const result = await emailjs.send(
          'service_o9r5lkl',
          'template_a3yljeg',
          templateParams
        );

        console.log('Email sent successfully:', result);

        // Track Meta Pixel Lead Event with Advanced Matching
        const { firstName: pixelFirstName, lastName: pixelLastName } = splitName(formData.name);
        if (window.fbq) {
          window.fbq('track', 'Lead', {
            content_name: 'Contact Form Submission',
            content_category: formData.inquiry || 'general',
            value: 1.00,
            currency: 'EUR'
          }, {
            em: formData.email,
            fn: pixelFirstName,
            ln: pixelLastName,
            ph: formData.phone,
            external_id: formData.email
          });
          console.log('Meta Pixel Lead Event tracked');
        }

        setSubmitStatus('success');

        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            company: '',
            jobTitle: '',
            phone: '',
            inquiry: '',
            country: '',
            organization: '',
            assistance: '',
            message: ''
          });
          setSubmitStatus(null);
          if (!isPage && onClose) onClose();
        }, 2000);

      } catch (error) {
        console.error('EmailJS Error:', error);
        setSubmitStatus('error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (!isPage && !isOpen) return null;

  const formContent = (
    <div className={isPage ? "" : "p-3 sm:p-6 md:p-8"}>
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 md:space-y-6">
            {/* Row 1 - Name Fields */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              <div>
                <label className="block text-gray-700 text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                  {t('kontaktPage.form.name')} {t('kontaktPage.form.required')}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-gray-700 text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                  {t('kontaktPage.form.phone')} {t('kontaktPage.form.required')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                )}
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-gray-700 text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                  {t('kontaktPage.form.email')} {t('kontaktPage.form.required')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />

                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Row 2 - Company, Position, Inquiry */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              <div>
                <label className="block text-gray-700 text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                  {t('kontaktPage.form.company')} {t('kontaktPage.form.optional')}
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                  {t('kontaktPage.form.jobTitle')} {t('kontaktPage.form.optional')}
                </label>
                <input
                  type="text"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                  {t('kontaktPage.form.inquiry')} {t('kontaktPage.form.optional')}
                </label>
                <select
                  name="inquiry"
                  value={formData.inquiry}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white text-base"
                >
                  <option value="">{t('kontaktPage.form.placeholders.select')}</option>
                  <option value="demo">{t('kontaktPage.form.options.inquiry.demo')}</option>
                  <option value="partnership">{t('kontaktPage.form.options.inquiry.partnership')}</option>
                  <option value="other">{t('kontaktPage.form.options.inquiry.other')}</option>
                </select>
              </div>
            </div>

            {/* Row 3 - Country, Organization, Assistance */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              <div>
                <label className="block text-gray-700 text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                  {t('kontaktPage.form.country')} {t('kontaktPage.form.optional')}
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white text-base"
                >
                  <option value="">{t('kontaktPage.form.placeholders.select')}</option>
                  <option value="de">{t('kontaktPage.form.options.country.de')}</option>
                  <option value="uk">{t('kontaktPage.form.options.country.uk')}</option>
                  <option value="other">{t('kontaktPage.form.options.country.other')}</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                  {t('kontaktPage.form.organization')} {t('kontaktPage.form.optional')}
                </label>
                <select
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white text-base"
                >
                  <option value="">{t('kontaktPage.form.placeholders.select')}</option>
                  <option value="transit">{t('kontaktPage.form.options.organization.transit')}</option>
                  <option value="government">{t('kontaktPage.form.options.organization.government')}</option>
                  <option value="private">{t('kontaktPage.form.options.organization.private')}</option>
                  <option value="consultant">{t('kontaktPage.form.options.organization.consultant')}</option>
                  <option value="other">{t('kontaktPage.form.options.organization.other')}</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                  {t('kontaktPage.form.assistance')} {t('kontaktPage.form.optional')}
                </label>
                <select
                  name="assistance"
                  value={formData.assistance}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white text-base"
                >
                  <option value="">{t('kontaktPage.form.placeholders.select')}</option>
                  <option value="all">{t('kontaktPage.form.options.assistance.all')}</option>
                  <option value="planning">{t('kontaktPage.form.options.assistance.planning')}</option>
                  <option value="scheduling">{t('kontaktPage.form.options.assistance.scheduling')}</option>
                  <option value="operations">{t('kontaktPage.form.options.assistance.operations')}</option>
                  <option value="other">{t('kontaktPage.form.options.assistance.other')}</option>
                </select>
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-gray-700 text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                {t('kontaktPage.form.message')} {t('kontaktPage.form.optional')}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className="w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              ></textarea>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                ✓ Ihre Anfrage wurde erfolgreich gesendet!
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                ✗ Fehler beim Senden. Bitte versuchen Sie es erneut.
              </div>
            )}

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`bg-gradient-to-r from-blue-500 to-orange-500 hover:from-blue-600 hover:to-orange-600 text-white font-bold py-2.5 sm:py-3 md:py-4 px-8 sm:px-10 md:px-12 rounded-full transition-all duration-300 hover:shadow-xl transform hover:scale-105 text-sm sm:text-base ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Wird gesendet...' : t('kontaktPage.submit')}
              </button>
            </div>
          </form>

          {/* Alternative Contact Info */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center text-gray-600 text-sm">
            <p>
              Alternativ können Sie sich direkt an den Vertrieb unter der{' '}
              <a href="tel:+4015901172955" className="text-blue-600 hover:text-blue-800 font-medium">
                +49 40 573 08474
              </a>{' '}
              oder der{' '}
              <a href="mailto:vertrieb@bus-agent.de" className="text-blue-600 hover:text-blue-800 font-medium">
                vertrieb@bus-agent.de
              </a>{' '}
              wenden.
            </p>
          </div>
        </div>
  );

  if (isPage) {
    return formContent;
  }

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-start justify-center bg-black bg-opacity-50 p-2 sm:p-4 pt-24 sm:pt-28 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-3xl lg:max-w-4xl mb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white border-b border-gray-200 px-3 sm:px-6 md:px-8 py-3 sm:py-4 flex justify-between items-center rounded-t-xl sm:rounded-t-2xl">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-800">
            {t('kontaktPage.hero.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl leading-none flex-shrink-0 ml-2"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="p-3 sm:p-6 md:p-8">
          {formContent}
        </div>
      </div>
    </div>
  );
};

export default ContactFormModal;
